import { LandingHeader } from "../components/landing/LandingHeader";
import { LandingFooter } from "../components/landing/LandingFooter";

export function PrivacyPolicyPage() {
  return (
    <div className="min-h-screen flex flex-col">
      <LandingHeader />
      <main className="flex-1 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
          <h1 className="text-4xl font-bold text-gray-900 mb-8">
            Privacy Policy
          </h1>

          <div className="prose prose-gray max-w-none">
            <p className="text-gray-600 mb-8">Last updated: January 22, 2025</p>

            {/* Section 1: Introduction */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                1. Introduction
              </h2>
              <p className="text-gray-600 mb-4">
                RunLens is operated by Case Sensitive Paweł Pikuła, a company
                registered in Poland, European Union. We are committed to
                protecting your privacy and processing your personal data in
                accordance with the General Data Protection Regulation (GDPR)
                and applicable Polish data protection laws.
              </p>
              <p className="text-gray-600 mb-4">
                This Privacy Policy explains how we collect, use, disclose, and
                safeguard your information when you use our AI-powered running
                analysis service. Please read this policy carefully to
                understand our practices regarding your personal data.
              </p>
            </section>

            {/* Section 2: Information We Collect */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                2. Information We Collect
              </h2>

              <h3 className="text-xl font-medium text-gray-900 mb-3">
                2.1 Account Information
              </h3>
              <p className="text-gray-600 mb-2">
                When you create an account, we collect:
              </p>
              <ul className="list-disc pl-6 text-gray-600 mb-4 space-y-2">
                <li>Email address (required for account creation)</li>
                <li>
                  Password (securely hashed, never stored in plain text)
                </li>
                <li>Display name (optional)</li>
                <li>Profile biography (optional, maximum 500 characters)</li>
                <li>Profile avatar image (optional)</li>
              </ul>

              <h3 className="text-xl font-medium text-gray-900 mb-3">
                2.2 Running Activity Data
              </h3>
              <p className="text-gray-600 mb-2">
                When you connect your Garmin Connect or Strava account, we
                collect the following running activity data:
              </p>
              <ul className="list-disc pl-6 text-gray-600 mb-4 space-y-2">
                <li>
                  Activity metadata (name, date, time, duration, distance)
                </li>
                <li>
                  Performance metrics (pace, heart rate, maximum heart rate,
                  calories burned)
                </li>
                <li>Elevation data (gain and loss)</li>
                <li>
                  Training effect metrics (aerobic and anaerobic training effect
                  - Garmin only)
                </li>
                <li>Heart rate zone distribution and time in each zone</li>
                <li>
                  Time-series data samples (heart rate, pace, speed, and
                  elevation recorded throughout your activity)
                </li>
                <li>GPS route data (if available from the source platform)</li>
              </ul>
              <p className="text-gray-600 mb-4">
                <strong>Note:</strong> We only sync running activities (running,
                trail running, treadmill running). Other activity types such as
                cycling or swimming are not collected.
              </p>

              <h3 className="text-xl font-medium text-gray-900 mb-3">
                2.3 AI Interaction Data
              </h3>
              <p className="text-gray-600 mb-2">
                When you use our AI coaching features, we collect:
              </p>
              <ul className="list-disc pl-6 text-gray-600 mb-4 space-y-2">
                <li>Your chat messages and questions to the AI coach</li>
                <li>AI-generated insights and recommendations</li>
                <li>
                  Conversation history to provide contextual coaching responses
                </li>
              </ul>

              <h3 className="text-xl font-medium text-gray-900 mb-3">
                2.4 Technical Data
              </h3>
              <ul className="list-disc pl-6 text-gray-600 mb-4 space-y-2">
                <li>
                  Authentication tokens for connected services (encrypted at
                  rest)
                </li>
                <li>Session information for maintaining your login state</li>
                <li>
                  Integration connection status and synchronization timestamps
                </li>
              </ul>
            </section>

            {/* Section 3: How We Use Your Information */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                3. How We Use Your Information
              </h2>
              <p className="text-gray-600 mb-4">
                We use your information for the following purposes:
              </p>
              <ul className="list-disc pl-6 text-gray-600 mb-4 space-y-2">
                <li>
                  <strong>Service Provision:</strong> Synchronizing your running
                  activities from connected platforms and displaying them in
                  your dashboard
                </li>
                <li>
                  <strong>AI-Powered Insights:</strong> Analyzing your activity
                  data to generate personalized training insights,
                  recommendations, and coaching responses
                </li>
                <li>
                  <strong>Contextual Coaching:</strong> Storing conversation
                  history to provide relevant and personalized AI coaching based
                  on your training patterns
                </li>
                <li>
                  <strong>Service Improvement:</strong> Understanding usage
                  patterns to improve our features and user experience
                </li>
                <li>
                  <strong>Communication:</strong> Sending important service
                  updates and responding to your support requests
                </li>
                <li>
                  <strong>Security:</strong> Protecting your account and
                  detecting potential security threats
                </li>
              </ul>

              <h3 className="text-xl font-medium text-gray-900 mb-3">
                3.1 AI Processing Disclosure
              </h3>
              <p className="text-gray-600 mb-4">
                To provide AI-powered insights, your activity data and chat
                messages are processed by OpenAI's language models. When you
                request insights or chat with the AI coach, we send relevant
                activity data (such as performance metrics, duration, and
                training patterns) along with your messages to generate
                personalized responses.
              </p>
              <p className="text-gray-600 mb-4">
                <strong>Important:</strong> AI-generated insights are for
                informational and educational purposes only. They do not
                constitute medical advice, and you should consult a healthcare
                professional before making decisions that affect your health or
                training regimen.
              </p>
            </section>

            {/* Section 4: Data Sharing and Third-Party Services */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                4. Data Sharing and Third-Party Services
              </h2>
              <p className="text-gray-600 mb-4">
                We do not sell your personal information. We share your data
                only with the following service providers who help us operate
                RunLens:
              </p>

              <div className="overflow-x-auto mb-4">
                <table className="min-w-full text-sm text-gray-600">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-2 pr-4 font-semibold text-gray-900">
                        Service
                      </th>
                      <th className="text-left py-2 pr-4 font-semibold text-gray-900">
                        Purpose
                      </th>
                      <th className="text-left py-2 pr-4 font-semibold text-gray-900">
                        Data Shared
                      </th>
                      <th className="text-left py-2 font-semibold text-gray-900">
                        Location
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b border-gray-100">
                      <td className="py-2 pr-4">Supabase</td>
                      <td className="py-2 pr-4">Database & Authentication</td>
                      <td className="py-2 pr-4">
                        Account data, activities, encrypted tokens
                      </td>
                      <td className="py-2">EU (Frankfurt)</td>
                    </tr>
                    <tr className="border-b border-gray-100">
                      <td className="py-2 pr-4">Deno Deploy</td>
                      <td className="py-2 pr-4">
                        Application Hosting & Caching
                      </td>
                      <td className="py-2 pr-4">
                        Session data, AI insights cache
                      </td>
                      <td className="py-2">Global (Edge)</td>
                    </tr>
                    <tr className="border-b border-gray-100">
                      <td className="py-2 pr-4">OpenAI</td>
                      <td className="py-2 pr-4">AI Processing</td>
                      <td className="py-2 pr-4">
                        Activity data, chat messages
                      </td>
                      <td className="py-2">USA</td>
                    </tr>
                    <tr className="border-b border-gray-100">
                      <td className="py-2 pr-4">Garmin Connect</td>
                      <td className="py-2 pr-4">Activity Synchronization</td>
                      <td className="py-2 pr-4">
                        OAuth tokens (to fetch your data)
                      </td>
                      <td className="py-2">USA</td>
                    </tr>
                    <tr className="border-b border-gray-100">
                      <td className="py-2 pr-4">Strava</td>
                      <td className="py-2 pr-4">Activity Synchronization</td>
                      <td className="py-2 pr-4">
                        OAuth tokens (to fetch your data)
                      </td>
                      <td className="py-2">USA</td>
                    </tr>
                    <tr>
                      <td className="py-2 pr-4">Google Analytics</td>
                      <td className="py-2 pr-4">Usage Analytics</td>
                      <td className="py-2 pr-4">
                        Anonymous usage data, page views, interactions
                      </td>
                      <td className="py-2">USA</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <p className="text-gray-600 mb-4">
                We may also disclose your information when required by law or to
                protect our rights, privacy, safety, or property.
              </p>
            </section>

            {/* Section 5: Data Security */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                5. Data Security
              </h2>
              <p className="text-gray-600 mb-4">
                We implement appropriate technical and organizational measures
                to protect your personal data:
              </p>
              <ul className="list-disc pl-6 text-gray-600 mb-4 space-y-2">
                <li>
                  All data is transmitted over HTTPS using TLS encryption
                </li>
                <li>
                  Passwords are hashed using industry-standard algorithms and
                  never stored in plain text
                </li>
                <li>
                  OAuth tokens for connected services are encrypted at rest
                </li>
                <li>
                  Row-Level Security (RLS) ensures you can only access your own
                  data
                </li>
                <li>
                  Regular security reviews and updates to address potential
                  vulnerabilities
                </li>
              </ul>
              <p className="text-gray-600 mb-4">
                While we strive to protect your personal data, no method of
                transmission over the Internet or electronic storage is 100%
                secure. We cannot guarantee absolute security.
              </p>
            </section>

            {/* Section 6: Your Rights Under GDPR */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                6. Your Rights Under GDPR
              </h2>
              <p className="text-gray-600 mb-4">
                As a data subject under GDPR, you have the following rights:
              </p>
              <ul className="list-disc pl-6 text-gray-600 mb-4 space-y-2">
                <li>
                  <strong>Right of Access (Article 15):</strong> Request a copy
                  of the personal data we hold about you
                </li>
                <li>
                  <strong>Right to Rectification (Article 16):</strong> Request
                  correction of inaccurate or incomplete data
                </li>
                <li>
                  <strong>Right to Erasure (Article 17):</strong> Request
                  deletion of your personal data ("right to be forgotten")
                </li>
                <li>
                  <strong>Right to Restriction (Article 18):</strong> Request
                  limitation of processing of your data
                </li>
                <li>
                  <strong>Right to Data Portability (Article 20):</strong>{" "}
                  Request your data in a structured, machine-readable format
                </li>
                <li>
                  <strong>Right to Object (Article 21):</strong> Object to
                  processing of your personal data
                </li>
                <li>
                  <strong>Right to Withdraw Consent:</strong> Disconnect
                  third-party integrations or delete your account at any time
                </li>
              </ul>

              <h3 className="text-xl font-medium text-gray-900 mb-3">
                How to Exercise Your Rights
              </h3>
              <p className="text-gray-600 mb-4">
                To exercise any of these rights, please contact us at{" "}
                <a
                  href="mailto:privacy@runlens.ai"
                  className="text-blue-600 hover:underline"
                >
                  privacy@runlens.ai
                </a>
                . We will respond to your request within 30 days. We may need to
                verify your identity before processing certain requests.
              </p>
            </section>

            {/* Section 7: Data Retention */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                7. Data Retention
              </h2>
              <p className="text-gray-600 mb-4">
                We retain your personal data as follows:
              </p>
              <ul className="list-disc pl-6 text-gray-600 mb-4 space-y-2">
                <li>
                  <strong>Active Account:</strong> Your data is retained for as
                  long as your account remains active
                </li>
                <li>
                  <strong>After Deletion Request:</strong> We retain your data
                  for 30 days following a deletion request to allow for account
                  recovery, after which it is permanently deleted
                </li>
                <li>
                  <strong>AI Conversation History:</strong> Deleted when your
                  account is deleted
                </li>
                <li>
                  <strong>Cached Insights:</strong> Deleted when your account is
                  deleted
                </li>
                <li>
                  <strong>Backups:</strong> Purged within 30 days of account
                  deletion
                </li>
              </ul>
              <p className="text-gray-600 mb-4">
                You can delete your account at any time through your account
                settings or by contacting us.
              </p>
            </section>

            {/* Section 8: International Data Transfers */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                8. International Data Transfers
              </h2>
              <p className="text-gray-600 mb-4">
                Your personal data may be transferred to and processed in
                countries outside the European Economic Area (EEA), including
                the United States, where our service providers OpenAI, Garmin,
                and Strava are located.
              </p>
              <p className="text-gray-600 mb-4">
                When we transfer your data outside the EEA, we ensure
                appropriate safeguards are in place, including:
              </p>
              <ul className="list-disc pl-6 text-gray-600 mb-4 space-y-2">
                <li>Standard Contractual Clauses approved by the European Commission</li>
                <li>Adequacy decisions by the European Commission</li>
                <li>Other legally recognized transfer mechanisms</li>
              </ul>
              <p className="text-gray-600 mb-4">
                By using RunLens and connecting third-party services, you
                consent to these international data transfers.
              </p>
            </section>

            {/* Section 9: Cookies and Local Storage */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                9. Cookies and Local Storage
              </h2>
              <p className="text-gray-600 mb-4">
                We use the following cookies and local storage:
              </p>
              <ul className="list-disc pl-6 text-gray-600 mb-4 space-y-2">
                <li>
                  <strong>Session Cookies:</strong> To maintain your
                  authenticated session
                </li>
                <li>
                  <strong>Local Storage:</strong> To store your UI preferences
                  and settings
                </li>
                <li>
                  <strong>Google Analytics Cookies:</strong> To collect
                  anonymous usage statistics and help us understand how visitors
                  use our service. This includes cookies such as _ga, _gid, and
                  _gat. Data collected is aggregated and anonymous.
                </li>
              </ul>
              <p className="text-gray-600 mb-4">
                We do not use:
              </p>
              <ul className="list-disc pl-6 text-gray-600 mb-4 space-y-2">
                <li>Advertising cookies</li>
                <li>Third-party tracking cookies for marketing purposes</li>
              </ul>
              <p className="text-gray-600 mb-4">
                You can opt out of Google Analytics by installing the{" "}
                <a
                  href="https://tools.google.com/dlpage/gaoptout"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:underline"
                >
                  Google Analytics Opt-out Browser Add-on
                </a>
                .
              </p>
            </section>

            {/* Section 10: Children's Privacy */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                10. Children's Privacy
              </h2>
              <p className="text-gray-600 mb-4">
                RunLens is not intended for users under the age of 16. We do not
                knowingly collect personal data from children under 16. If you
                are a parent or guardian and believe your child has provided us
                with personal data, please contact us at{" "}
                <a
                  href="mailto:privacy@runlens.ai"
                  className="text-blue-600 hover:underline"
                >
                  privacy@runlens.ai
                </a>
                , and we will take steps to delete such information.
              </p>
            </section>

            {/* Section 11: Health and Medical Disclaimer */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                11. Health and Medical Disclaimer
              </h2>
              <p className="text-gray-600 mb-4">
                RunLens provides AI-generated training insights and
                recommendations for informational and educational purposes only.
                This information:
              </p>
              <ul className="list-disc pl-6 text-gray-600 mb-4 space-y-2">
                <li>Is not intended as medical advice</li>
                <li>
                  Should not be used as a substitute for professional medical
                  advice, diagnosis, or treatment
                </li>
                <li>
                  Does not replace consultation with qualified healthcare
                  providers or certified coaches
                </li>
              </ul>
              <p className="text-gray-600 mb-4">
                Always consult a physician or qualified healthcare professional
                before starting any new exercise program or making changes to
                your training based on insights provided by RunLens.
              </p>
            </section>

            {/* Section 12: Changes to This Policy */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                12. Changes to This Policy
              </h2>
              <p className="text-gray-600 mb-4">
                We may update this Privacy Policy from time to time to reflect
                changes in our practices or for legal, operational, or
                regulatory reasons.
              </p>
              <p className="text-gray-600 mb-4">
                For material changes, we will notify you by email or through a
                prominent notice on our service before the changes take effect.
                The "Last updated" date at the top of this policy indicates when
                it was last revised.
              </p>
              <p className="text-gray-600 mb-4">
                Your continued use of RunLens after changes to this Privacy
                Policy constitutes your acceptance of the updated terms.
              </p>
            </section>

            {/* Section 13: Contact Us */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                13. Contact Us
              </h2>
              <p className="text-gray-600 mb-4">
                If you have questions about this Privacy Policy or wish to
                exercise your data protection rights, please contact us:
              </p>
              <div className="text-gray-600 mb-4">
                <p>
                  <strong>Case Sensitive Paweł Pikuła</strong>
                </p>
                <p>
                  Email:{" "}
                  <a
                    href="mailto:privacy@runlens.ai"
                    className="text-blue-600 hover:underline"
                  >
                    privacy@runlens.ai
                  </a>
                </p>
              </div>

              <h3 className="text-xl font-medium text-gray-900 mb-3">
                Supervisory Authority
              </h3>
              <p className="text-gray-600 mb-4">
                If you are not satisfied with our response or believe we are
                processing your personal data unlawfully, you have the right to
                lodge a complaint with the Polish Data Protection Authority:
              </p>
              <div className="text-gray-600 mb-4">
                <p>
                  <strong>
                    Urząd Ochrony Danych Osobowych (UODO)
                  </strong>
                </p>
                <p>ul. Stawki 2, 00-193 Warszawa, Poland</p>
                <p>
                  Website:{" "}
                  <a
                    href="https://uodo.gov.pl"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline"
                  >
                    https://uodo.gov.pl
                  </a>
                </p>
              </div>
            </section>
          </div>
        </div>
      </main>
      <LandingFooter />
    </div>
  );
}
